import { useMemo } from "react";

function seeded(seed) {
    return function () {
        seed |= 0;
        seed = (seed + 0x6d2b79f5) | 0;
        let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function contourPath(cx, cy, r, wobble, phase) {
    const pts = [];
    for (let a = 0; a <= 72; a++) {
        const ang = (a / 72) * Math.PI * 2;
        const rr = r * (1 + wobble * Math.sin(ang * 3 + phase) + wobble * 0.55 * Math.sin(ang * 5 - phase * 1.7));
        pts.push(`${(cx + Math.cos(ang) * rr * 1.55).toFixed(1)} ${(cy + Math.sin(ang) * rr).toFixed(1)}`);
    }
    return `M ${pts.join(" L ")}`;
}

function Contours() {
    const paths = useMemo(() => {
        const out = [];
        const centers = [
            { cx: 330, cy: 330, gold: false },
            { cx: 1130, cy: 600, gold: true },
        ];
        centers.forEach((c, ci) => {
            for (let k = 0; k < 7; k++) {
                out.push({
                    d: contourPath(c.cx, c.cy, 46 + k * 44, 0.16 + k * 0.012, ci * 2.1 + k * 0.9),
                    color: ci === 1 && k === 3 ? "#D7B06A" : "#1687FF",
                    opacity: 0.1 - k * 0.008,
                });
            }
        });
        return out;
    }, []);
    return (
        <svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" className="w-full h-full">
            {paths.map((p, i) => (
                <path key={i} d={p.d} fill="none" stroke={p.color} strokeOpacity={p.opacity} strokeWidth="1" />
            ))}
        </svg>
    );
}

function Constellation() {
    const { nodes, links } = useMemo(() => {
        const rand = seeded(7);
        const pts = Array.from({ length: 18 }, () => ({ x: rand() * 1440, y: rand() * 900, r: 1 + rand() * 1.6, d: rand() * 4 }));
        const links = [];
        pts.forEach((a, i) => {
            pts.forEach((b, j) => {
                if (j <= i) return;
                const dx = a.x - b.x;
                const dy = a.y - b.y;
                if (Math.sqrt(dx * dx + dy * dy) < 250) links.push([a, b]);
            });
        });
        return { nodes: pts, links };
    }, []);
    return (
        <svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full">
            {links.map(([a, b], i) => (
                <line key={`l-${i}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#1687FF" strokeOpacity="0.07" strokeWidth="1" />
            ))}
            {nodes.map((n, i) => (
                <circle key={`n-${i}`} cx={n.x} cy={n.y} r={n.r} fill={i % 5 === 0 ? "#D7B06A" : "#55B7FF"} opacity="0.35" style={{ animation: `pulse-soft ${3 + n.d}s ease-in-out ${n.d}s infinite` }} />
            ))}
        </svg>
    );
}

function Particles() {
    const particles = useMemo(() => {
        const rand = seeded(3);
        return Array.from({ length: 26 }, (_, i) => {
            const gold = i % 3 === 0;
            return {
                left: rand() * 100,
                size: gold ? 3 : 2 + rand() * 1.5,
                dur: 16 + rand() * 22,
                delay: -rand() * 30,
                drift: (rand() - 0.5) * 90,
                opacity: 0.25 + rand() * 0.4,
                gold,
            };
        });
    }, []);
    return (
        <>
            {particles.map((p, i) => (
                <span
                    key={i}
                    className="capital-particle"
                    style={{
                        left: `${p.left}%`,
                        width: p.size,
                        height: p.size,
                        background: p.gold ? "#D7B06A" : "#55B7FF",
                        boxShadow: p.gold ? "0 0 8px rgba(215,176,106,0.8)" : "0 0 8px rgba(85,183,255,0.7)",
                        animationDuration: `${p.dur}s`,
                        animationDelay: `${p.delay}s`,
                        "--o": p.opacity,
                        "--dx": `${p.drift}px`,
                    }}
                />
            ))}
        </>
    );
}

export default function BackgroundField() {
    return (
        <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true" data-testid="vera-background-field">
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(120% 90% at 72% 8%, rgba(10,27,51,0.6), rgba(3,7,11,0) 60%), radial-gradient(90% 70% at 12% 88%, rgba(10,27,51,0.55), rgba(3,7,11,0) 55%)",
                }}
            />
            <div className="vera-horizon" />
            <div className="absolute inset-[-60px] opacity-60 animate-[contourdrift_110s_ease-in-out_infinite_alternate]">
                <Contours />
            </div>
            <Constellation />
            <Particles />
        </div>
    );
}
